<template>
   <Form ref="formRef" v-model="formData" @submit="handleSubmit" @dirty="(e) => (dirty = e)">
      <div class="__form-row">
         <div
            class="rounded-small __clickable mx-auto size-[30%] shrink-0 overflow-hidden p-2 max-md:min-w-[100px] md:size-20"
            @click="fileDialog.open()"
         >
            <Image
               class="size-full object-cover"
               :src="getFileUrlFromBucket(organization?.propic_url)"
            />
         </div>
         <TranslationInput grow v-bind="formData.name" v-model="formData.name.value" />
      </div>
      <TextInput grow v-bind="formData.aquiculturist" v-model="formData.aquiculturist.value" />

      <div class="__form-row">
         <TextInput grow v-bind="formData.email" v-model="formData.email.value" />
         <TextInput grow v-bind="formData.phone" v-model="formData.phone.value" />
      </div>

      <div class="__form-row">
         <TextInput
            grow
            v-bind="formData.physical_permit_activation_code"
            v-model="formData.physical_permit_activation_code.value"
         />
         <TextInput grow v-bind="formData.vat_number" v-model="formData.vat_number.value" />
      </div>

      <TranslationInput v-bind="formData.contacts" v-model="formData.contacts.value" />
   </Form>
</template>

<script lang="ts" setup>
import { useFileDialog } from '@vueuse/core'
import type { Unpacked } from '@xleddyl/types'
import type Form from '~/components/core/Form.vue'
import { getFileUrlFromBucket } from '~/composables/api/helpers/get-file-url'
import { useUpdateOrganization } from '~/composables/api/mutations/update-organization'
import { useUpdateOrganizationPropic } from '~/composables/api/mutations/update-organization-propic'
import type { useOrganization } from '~/composables/api/queries/organization'
import { DESCRIPTION_LENGTH, NAME_LENGTH } from '~/utils/consts/const'

type Props = {
   organization?: Omit<
      Unpacked<NonNullable<ReturnType<typeof useOrganization>['data']['value']>>,
      ''
   >
}

const props = withDefaults(defineProps<Props>(), {})

const organizationId = useOrganizationId()
const rbac = useRbac()
const { t } = useI18n()

const dirty = ref<boolean>(false)
const formRef = ref<InstanceType<typeof Form>>()

const updateOrganizationPropic = useUpdateOrganizationPropic({
   confirmation: {
      type: 'save',
   },
})

const fileDialog = useFileDialog({
   accept: 'image/*',
   multiple: false,
})

fileDialog.onChange(async (files) => {
   await tryCatch(async () => {
      if (!files) return
      await updateOrganizationPropic.mutateAsync({
         file: files[0],
         organizationId: organizationId.value,
      })
   })
   fileDialog.reset()
})

const updateOrganization = useUpdateOrganization({
   confirmation: {
      type: 'save',
   },
})

const actionLoading = computed(() => updateOrganization.isPending.value)
const actionDisabled = computed(() => !dirty.value || formDisabled.value)
const formDisabled = computed(() => {
   return !rbac(['organization:update'])
})

const formData = useFormData(() => ({
   name: {
      value: props.organization?.name,
      label: t('form.organization-form.name'),
      type: 'translation',

      mode: 'text' as const,
      required: true,
      maxLength: NAME_LENGTH,
      disabled: formDisabled.value,
   },
   aquiculturist: {
      value: props.organization?.aquiculturist,
      label: t('form.organization-form.aquiculturist'),
      type: 'text',
      maxLength: NAME_LENGTH,
      disabled: formDisabled.value,
   },
   contacts: {
      value: props.organization?.contacts,
      label: t('form.organization-form.contacts'),
      type: 'translation',

      mode: 'rich-text' as const,
      required: true,
      maxLength: DESCRIPTION_LENGTH,
      disabled: formDisabled.value,
   },
   phone: {
      value: props.organization?.phone,
      label: t('form.organization-form.phone'),
      type: 'text' as const,
      required: true,
      disabled: formDisabled.value,
      validator: isPhone,
   },
   email: {
      value: props.organization?.email,
      label: t('form.organization-form.email'),
      type: 'email' as const,
      required: true,
      disabled: formDisabled.value,
      validator: isEmail,
   },
   vat_number: {
      value: props.organization?.vat_number,
      label: t('form.organization-form.vat-number'),
      type: 'text' as const,
      required: true,
      disabled: formDisabled.value,
   },
   physical_permit_activation_code: {
      value: props.organization?.physical_permit_activation_code,
      label: t('form.organization-form.physical-permit-activation-code'),
      type: 'text',
      required: true,
      disabled: formDisabled.value,
   },
}))

const handleSubmit = async (data: typeof formData) => {
   await tryCatch(async () => {
      if (!organizationId.value) return
      await updateOrganization.mutateAsync({
         p_organization_id: organizationId.value,
         p_name: data.name.value,
         p_aquiculturist: data.aquiculturist.value,
         p_contacts: data.contacts.value,
         p_email: data.email.value,
         p_phone: data.phone.value,
         p_vat_number: data.vat_number.value,
         p_physical_permit_activation_code: data.physical_permit_activation_code.value,
      })
   })
}

defineExpose({
   action: () => formRef.value?.submit(),
   loading: actionLoading,
   disabled: actionDisabled,
})
</script>
